import { useEffect } from "react";
import { Scale, ShieldCheck, Sparkles, Users } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Reveal } from "@/hooks/useReveal";
import { trackCTA } from "@/lib/tracking";
import { trackPlacementCTA } from "@/lib/cta-placement";
import { PRIMARY_CTAS } from "@/content/primary-cta";
import { StrategySessionApplicationForm } from "@/components/site/StrategySessionApplicationForm";
import { FAQSection } from "@/components/site/FAQSection";
import { buildFAQPageJsonLd, type FAQItem } from "@/content/faq";
import { organizationNode, publisherRef, SITE_URL } from "@/lib/entity-jsonld";

/**
 * Strategy-session FAQ. Rendered by <FAQSection> AND marked up as FAQPage from
 * the SAME array — Google requires every Q/A in structured data to be visible.
 */
const STRATEGY_FAQ: FAQItem[] = [
  {
    q: "What exactly is a strategy session?",
    a: "A private, focused working call with Dr. Sully (PsyD \u00b7 ThD \u00b7 DMin \u00b7 CERC). You bring the situation, the constraints, and the outcome you need. You leave with a clear read of the problem, the sequence to work it, and an honest answer about whether ongoing work together makes sense.",
  },
  {
    q: "Who is eligible to apply?",
    a: "Applications are open to adults 18 or older who are the decision-maker \u2014 or a direct influencer of the decision \u2014 for the situation described, and who can identify a defined outcome, a timeline, and an investment range. Individuals, founders, executives, pastors, boards, and family decision-makers are all welcome.",
  },
  {
    q: "Who is not a fit?",
    a: "This is not crisis care, emergency response, licensed psychotherapy, medical treatment, or legal representation. If you are in immediate danger or experiencing a mental-health emergency, call 911 or dial 988 (Suicide & Crisis Lifeline). Applications seeking free consulting, vendor pitches, or a guaranteed outcome are declined.",
  },
  {
    q: "Is applying a commitment to buy?",
    a: "No. The application is an intake, not a contract. If it is a fit, you receive session times and any applicable fee in writing before anything is scheduled. If it is not a fit, you get a plain answer \u2014 often with a referral or a next step you can run yourself.",
  },
  {
    q: "How fast will I hear back?",
    a: "Every application is read personally and answered within one business day. Intake is selective; when the calendar is full you will be told directly rather than left waiting.",
  },
  {
    q: "What should I prepare?",
    a: "The short version of the situation, what you have already tried, who else has to say yes, your real timeline, and the outcome that would make this worth it. Documents are optional at application stage and requested only if they are needed.",
  },
  {
    q: "Is what I share confidential?",
    a: "Application details are used only to evaluate fit and prepare for your session, and are never sold or shared for marketing. Confidentiality is not legal privilege, and disclosures involving abuse, neglect, or imminent danger to yourself or others may carry reporting obligations.",
  },
  {
    q: "Are results or outcomes guaranteed?",
    a: "No. No result, revenue figure, hire, ruling, funding decision, healing timeline, or institutional outcome is promised or guaranteed. What is provided is preparation, strategy, honest counsel, and experienced judgment. Decisions and results rest with you and with systems outside any advisor's control.",
  },
  {
    q: "Do you work with faith-led organizations?",
    a: "Yes. The work is faith-integrated and trauma-informed by default, and it serves individuals and organizations of any denomination, gender, or background \u2014 no discrimination, ever.",
  },
];

/** Legal / ethical disclaimer \u2014 exact language shown on the page. */
const DISCLAIMER = [
  "Eligibility: Applicants must be 18 years or older and must be the decision-maker, or a direct influencer of the decision, for the matter described. A defined outcome, timeline, and investment range are required for an application to be evaluated.",
  "Not clinical, legal, or medical services: A strategy session is professional advisory and coaching work. It is not psychotherapy, psychiatric care, medical diagnosis or treatment, legal advice, or legal representation, and it does not create a therapist-client, physician-patient, or attorney-client relationship.",
  "Not emergency or crisis services: No emergency, on-call, or crisis-stabilization coverage is provided. If you are in immediate danger or crisis, call 911 or dial 988 (Suicide & Crisis Lifeline).",
  "No guarantee of outcomes: No result is guaranteed. Nothing on this page or in any session should be construed as a promise or guarantee of financial, professional, clinical, spiritual, educational, legal, or institutional outcomes. Individual results vary and depend on factors outside of Dr. Sully's control.",
  "Confidentiality and its limits: Information you submit is used solely to evaluate fit and prepare for your session and is never sold or shared for marketing. Confidentiality is not legal privilege, and disclosures involving abuse, neglect, or imminent risk of harm may trigger mandatory reporting duties.",
  "Ethical standards and referral: Engagements are accepted only where there is genuine fit and competence. Where a matter calls for licensed clinical, medical, or legal expertise, a referral is made rather than the work being taken on.",
  "Scope and fees in writing: The scope, format, duration, and any fee for a session are confirmed in writing before scheduling. Submitting an application creates no obligation on either side.",
];

const STRATEGY_CTA = PRIMARY_CTAS.strategySession;

/** Shared anchor CTA — identical label/destination at every placement. */
const ApplyCTA = ({
  placement,
  surface,
}: {
  placement: "hero" | "mid_page" | "final";
  surface: string;
}) => (
  <a
    href={STRATEGY_CTA.href}
    onClick={() => trackPlacementCTA(placement, STRATEGY_CTA, { surface })}
    className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 shadow-cta hover:brightness-110 transition-all"
  >
    {STRATEGY_CTA.label}
  </a>
);

const TRUST = [
  { icon: ShieldCheck, label: "Selective intake" },
  { icon: Sparkles, label: "Strategy, not sales" },
  { icon: Users, label: "1 business-day response" },
];

const StrategySession = () => {
  useEffect(() => {
    trackCTA("strategy_session_view", { surface: "strategy_session_page" });
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title="Apply for a Strategy Session | Dr. Sully"
        description="Apply for a private strategy session with Dr. Sully (PsyD · ThD · DMin · CERC). Selective intake, one business-day response, strategy over sales."
        path="/strategy-session"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            // Canonical brand entity, matching index.html — the service and FAQ
            // below both attribute back to this single Organization @id.
            organizationNode(),
            {
              "@type": "Service",
              "@id": `${SITE_URL}/strategy-session#service`,
              name: "Private Strategy Session",
              serviceType: "Executive strategy consulting",
              description:
                "A private, selectively booked strategy session with Dr. Sully (PsyD \u00b7 ThD \u00b7 DMin \u00b7 CERC) to pressure-test the situation, timeline, and outcome before any engagement.",
              url: `${SITE_URL}/strategy-session`,
              provider: publisherRef,
              areaServed: "US",
              availableChannel: {
                "@type": "ServiceChannel",
                serviceUrl: `${SITE_URL}/strategy-session`,
                availableLanguage: "en",
              },
            },
            {
              ...buildFAQPageJsonLd(
                STRATEGY_FAQ,
                `${SITE_URL}/strategy-session#faq`,
              ),
              publisher: publisherRef,
            },
          ],
        }}
      />
      <SiteNav />

      <section className="pt-32 pb-14 md:pt-40 md:pb-16 bg-hero">
        <div className="container max-w-3xl text-center">
          <Reveal variant="up">
            <span className="eyebrow !mx-auto inline-flex">Private Strategy Session</span>
            <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-4 leading-[1.05]">
              Tell us the situation. <br className="hidden md:block" />
              <span className="text-gradient-gold">We'll confirm the fit.</span>
            </h1>
            <p className="text-silver text-base md:text-lg mt-6 leading-relaxed">
              Every application is read personally. Share the context, timeline,
              and outcome you're after — if it's a fit, you'll get session times
              within one business day.
            </p>
            <div className="mt-8">
              <ApplyCTA placement="hero" surface="strategy_session_hero" />
            </div>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-4 text-[10px] uppercase tracking-[0.2em] text-silver">
              {TRUST.map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-1.5">
                  <Icon className="h-3 w-3 text-gold" /> {label}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="container max-w-3xl">
          <Reveal variant="up">
            <StrategySessionApplicationForm id="apply" source="strategy_session_page" />
          </Reveal>
        </div>
      </section>

      <section className="pb-14 md:pb-20">
        <div className="container max-w-3xl text-center">
          <ApplyCTA placement="mid_page" surface="strategy_session_mid" />
        </div>
      </section>

      <FAQSection
        items={STRATEGY_FAQ}
        slugPrefix="strategy-faq"
        eyebrow="Strategy Sessions \u00b7 Frequently Asked"
        heading="What to expect before you apply."
        subheading="Fit, eligibility, confidentiality, and outcomes \u2014 answered plainly, before you send anything."
      />

      <section className="py-14 md:py-20 border-t border-fg-muted/10 bg-bg-secondary">
        <div className="container max-w-3xl">
          <Reveal variant="up">
            <span className="eyebrow inline-flex">Legal & Ethical Disclaimer</span>
            <h2 className="font-display text-2xl md:text-3xl mt-4">
              Eligibility, boundaries, and the no-guarantee standard.
            </h2>
            <ul className="mt-7 space-y-4">
              {DISCLAIMER.map((item) => {
                const [label, ...rest] = item.split(": ");
                return (
                  <li key={item} className="flex gap-3 text-sm leading-relaxed text-fg-secondary">
                    <Scale className="h-4 w-4 text-gold shrink-0 mt-1" aria-hidden="true" />
                    <span>
                      <strong className="text-foreground font-semibold">{label}:</strong>{" "}
                      {rest.join(": ")}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-20 border-t border-fg-muted/10 text-center">
        <div className="container max-w-2xl">
          <h2 className="font-display uppercase text-2xl md:text-3xl">
            Ready to put the situation in front of someone who has seen it before?
          </h2>
          <div className="mt-7">
            <ApplyCTA placement="final" surface="strategy_session_final" />
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default StrategySession;
